import { DateTime } from 'luxon';

//This function returns the array of captions.
export const getCaptions = (statement, head, row, markupTableData) => {
    if (markupTableData?.[statement]?.[head]?.[row]) {
        const captions = [...Object.keys(markupTableData?.[statement]?.[head]?.[row]?.["ALTERNATE CAPTIONS"] || {}), row];
        return captions
    } else {
        markupTableData?.[statement]?.[head] && Object.entries(markupTableData?.[statement]?.[head])?.map(([itemKey, itemValue], ind) => {
            if (itemValue["ALTERNATE CAPTIONS"] && itemValue["ALTERNATE CAPTIONS"][row]) {
                let captions = [...Object.keys(itemValue?.["ALTERNATE CAPTIONS"] || {}), itemKey];
                return captions
            }
        })
    }
};

export const getHeadStatement = (head, markupTableData) => {
    if (!markupTableData) return null;

    const keyWithHead = Object.entries(markupTableData).find(
        ([, value]) => head in value
    );

    return keyWithHead ? keyWithHead[0] : null;
};

const parseDate = (dateString) => {
    if (dateString.startsWith("YE")) {
        return new Date(dateString.replace("YE", "").trim());
    } else if (dateString.includes("To")) {
        // Take the end date of the range for sorting
        const range = dateString.split("To").map((date) => date.trim());
        return new Date(range[0]);
    }
    return new Date(dateString); // Default case
};

export const sortDateOptionHandler = (dateOptions) => {
    return [...dateOptions].sort((a, b) => {
        if (a.label === "New period") return -1; // "New period" always at the top
        if (b.label === "New period") return 1;

        // Parse and compare dates for sorting
        const dateA = parseDate(a.label);
        const dateB = parseDate(b.label);
        return dateB - dateA;
    });
};

export const validateEmail = (email) => {
    // Simple regex pattern for email validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
};

export const formatAmount = (amount, toFix = false) => {
    if (typeof amount !== 'number' || isNaN(amount) || amount === 0) {
        return "-";
    }

    // Use string conversion for fixed decimal display
    const formatted = toFix
        ? amount.toFixed(2) // returns string like "123.00"
        : amount.toLocaleString("en-US"); // returns number format
    
    if (amount >= 0) {
        return toFix ? Number(formatted).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : formatted;
    } else {
        return `(${toFix
            ? Number(Math.abs(formatted)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : Math.abs(amount).toLocaleString("en-US")})`;
    }
};

//  amount.toLocaleString("en-US", { 
//     minimumFractionDigits: 2, 
//     maximumFractionDigits: 2 
// });


/**
 * Groups journal entries by unique debit/credit account combinations
 * and aggregates their values (debit, credit, splitedRows, count).
 *
 * Function preserves original behaviour but:
 * - matches entries independent of account order
 * - safely merges values
 * - normalizes row structure
 * - prevents runtime crashes
 */
export const cfsUniqueWPHandler = (modifiedData) => {
    // Final grouped output
    const uniqueEntries = [];

    // Custom incremental index for grouped entries
    let customIndex = 0;


    /**
     * Normalize debit rows to maintain consistent structure
     * - debit rows should not contain credit values
     * - date should be replaced with "-"
     */
    const normalizeDebitRows = (rows = []) =>
        rows.map(r => ({
            ...r,
            credit: "",
            date: "-"
        }));


    /**
     * Normalize credit rows to maintain consistent structure
     * - credit rows should not contain debit values
     * - date should be replaced with "-"
     */
    const normalizeCreditRows = (rows = []) =>
        rows.map(r => ({
            ...r,
            debit: "",
            date: "-"
        }));


    /**
     * Creates a unique matching key for account sets.
     * Sorting ensures order-independent comparison.
     *
     * Example:
     *  ["Cash", "Bank"] and ["Bank", "Cash"] produce same key
     */
    const getAccountSet = (rows = []) =>
        rows.map(r => r?.accountName).sort().join("|");


    /**
     * Merge debit values account-wise.
     * Each account is matched by name (not index).
     */
    const mergeDebitRows = (existingRows, incomingRows) => {

        const incomingGrouped = {};
        incomingRows.forEach(r => {
            if (!incomingGrouped[r.accountName]) {
                incomingGrouped[r.accountName] = [];
            }
            incomingGrouped[r.accountName].push(r);
        });

        const existingGrouped = {};
        existingRows.forEach(r => {
            if (!existingGrouped[r.accountName]) {
                existingGrouped[r.accountName] = [];
            }
            existingGrouped[r.accountName].push(r);
        });

        const merged = [];

        Object.keys(existingGrouped).forEach(accountName => {

            const existingGroup = existingGrouped[accountName];
            const incomingGroup = incomingGrouped[accountName] || [];

            const maxLength = Math.max(existingGroup.length, incomingGroup.length);

            for (let i = 0; i < maxLength; i++) {

                const existingRow = existingGroup[i] || {};
                const incomingRow = incomingGroup[i] || {};

                merged.push({
                    ...existingRow,
                    debit:
                        Number(existingRow.debit || 0) +
                        Number(incomingRow.debit || 0),
                    credit: "",
                    date: "-"
                });
            }
        });

        return merged;
    };



    /**
     * Merge credit rows while preserving duplicate account rows.
     * Matching is done per accountName AND occurrence index.
     */
    const mergeCreditRows = (existingRows, incomingRows) => {

        // group incoming rows by account name
        const incomingGrouped = {};
        incomingRows.forEach(r => {
            if (!incomingGrouped[r.accountName]) {
                incomingGrouped[r.accountName] = [];
            }
            incomingGrouped[r.accountName].push(r);
        });

        // group existing rows by account name
        const existingGrouped = {};
        existingRows.forEach(r => {
            if (!existingGrouped[r.accountName]) {
                existingGrouped[r.accountName] = [];
            }
            existingGrouped[r.accountName].push(r);
        });

        const merged = [];

        // merge per account group
        Object.keys(existingGrouped).forEach(accountName => {

            const existingGroup = existingGrouped[accountName];
            const incomingGroup = incomingGrouped[accountName] || [];

            const maxLength = Math.max(existingGroup.length, incomingGroup.length);

            for (let i = 0; i < maxLength; i++) {

                const existingRow = existingGroup[i] || {};
                const incomingRow = incomingGroup[i] || {};

                merged.push({
                    ...existingRow,
                    credit:
                        Number(existingRow.credit || 0) +
                        Number(incomingRow.credit || 0),
                    debit: "",
                    date: "-"
                });
            }
        });

        return merged;
    };



    /**
     * Merge split rows (paired debit-credit representation).
     * Values are matched by account name and accumulated.
     *
     * splitRows structure:
     * [
     *   [debitRow, creditRow],
     *   [debitRow, creditRow]
     * ]
     */
    const mergeSplitedRows = (existing = [], debitRows = [], creditRows = []) => {
        return existing.map(([d, c]) => {

            // Find matching debit contribution
            const debitMatch =
                debitRows.find(r => r.accountName === d.accountName)?.debit ||
                creditRows.find(r => r.accountName === d.accountName)?.credit ||
                0;

            // Find matching credit contribution
            const creditMatch =
                creditRows.find(r => r.accountName === c.accountName)?.credit ||
                debitRows.find(r => r.accountName === c.accountName)?.debit ||
                0;

            return [
                {
                    ...d,
                    debit: Number(d.debit || 0) + Number(debitMatch),
                    date: "-"
                },
                {
                    ...c,
                    credit: Number(c.credit || 0) + Number(creditMatch),
                    date: "-"
                }
            ];
        });
    };


    /**
     * Iterate through all journal entries
     * and group them by matching debit/credit account structure.
     */
    modifiedData.forEach((JournalEntry) => {

        const debitRows = JournalEntry?.debitRows || [];
        const creditRows = JournalEntry?.creditRows || [];

        // Generate matching keys for grouping
        const debitKey = getAccountSet(debitRows);
        const creditKey = getAccountSet(creditRows);

        /**
         * Find existing grouped entry where:
         * - same debit accounts
         * - same credit accounts
         * - same number of rows
         */
        const existingIndex = uniqueEntries.findIndex(entry =>
            getAccountSet(entry.debitRows) === debitKey &&
            getAccountSet(entry.creditRows) === creditKey &&
            entry.debitRows.length === debitRows.length &&
            entry.creditRows.length === creditRows.length
        );


        // =============================
        // MERGE WITH EXISTING GROUP
        // =============================
        if (existingIndex !== -1) {

            const existing = uniqueEntries[existingIndex];

            existing.count += 1;

            // Merge financial values
            existing.debitRows = mergeDebitRows(existing.debitRows, debitRows);
            existing.creditRows = mergeCreditRows(existing.creditRows, creditRows);
            existing.splitedRows = mergeSplitedRows(
                existing.splitedRows,
                debitRows,
                creditRows
            );

            // Preserve original entries
            existing.allEntries.push(JournalEntry);

        }

        // =============================
        // CREATE NEW GROUP
        // =============================
        else {

            customIndex += 1;

            uniqueEntries.push({
                ...JournalEntry,
                index: customIndex,
                count: 1,
                allEntries: [JournalEntry],

                // Store normalized rows
                debitRows: normalizeDebitRows(debitRows),
                creditRows: normalizeCreditRows(creditRows)
            });

        }

    });

    return uniqueEntries;
};

export const normalize = (str) => (str || "").trim().toLowerCase();

export const getCountMap = (rows = []) => {
    const map = {};
    rows.forEach(row => {
        const key = normalize(row.accountName);
        map[key] = (map[key] || 0) + 1;
    });
    return map;
};

export const isSameDebitStructure = (rows1, rows2) => {
    if (rows1.length !== rows2.length) return false;

    const map1 = getCountMap(rows1);
    const map2 = getCountMap(rows2);

    const keys1 = Object.keys(map1);
    const keys2 = Object.keys(map2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(key => map1[key] === map2[key]);
};

const buildIndexedMap = (rows = []) => {
    const map = {};
    const counter = {};

    rows.forEach((row) => {
        const key = row.accountName;

        const index = counter[key] || 0;
        counter[key] = index + 1;

        if (!map[key]) map[key] = [];
        map[key][index] = row;
    });

    return map;
};

const mergeEntries = ({
    entry,
    JournalEntry,
    debitRows,
    creditRows,
    periodKey
}) => {
    const newDebitMap = buildIndexedMap(debitRows);
    const newCreditMap = buildIndexedMap(creditRows);

    const existingDebitMap = buildIndexedMap(entry.debitRows);
    const existingCreditMap = buildIndexedMap(entry.creditRows);

    return {
        ...entry,

        // ✅ Count
        count:
            entry?.period === JournalEntry?.period
                ? entry.count + 1
                : entry.count,

        // ✅ Debit merge
        debitRows: entry.debitRows.map((debitRow) => {
            const key = debitRow.accountName;

            const occurrenceIndex =
                existingDebitMap[key]?.indexOf(debitRow);

            const matchedDebit =
                newDebitMap[key]?.[occurrenceIndex];

            const addDebit = Number(matchedDebit?.debit || 0);

            return {
                ...debitRow,

                [periodKey]: {
                    debit: (
                        Number(debitRow[periodKey]?.debit || 0) + addDebit
                    ).toFixed(2),
                    credit: "",
                    date: debitRow.date || "",
                },

                debit: (
                    Number(debitRow.debit || 0) + addDebit
                ).toFixed(2),

                credit: "",
                date: "-",

                cfsMethodInfo: {
                    ...debitRow?.cfsMethodInfo,
                    [periodKey]: (
                        debitRow?.cfsMethodInfo?.[periodKey] || []
                    ).map((method, idx) => ({
                        ...method,
                        amount: (
                            Number(method?.amount || 0) +
                            Number(
                                matchedDebit?.cfsMethodInfo?.[idx]?.amount || 0
                            )
                        ).toFixed(2),
                    })),
                },
            };
        }),

        // ✅ Credit merge
        creditRows: entry.creditRows.map((creditRow) => {
            const key = creditRow.accountName;

            const occurrenceIndex =
                existingCreditMap[key]?.indexOf(creditRow);

            const matchedCredit =
                newCreditMap[key]?.[occurrenceIndex];

            const addCredit = Number(matchedCredit?.credit || 0);

            return {
                ...creditRow,

                [periodKey]: {
                    debit: "",
                    credit: (
                        Number(creditRow[periodKey]?.credit || 0) +
                        addCredit
                    ).toFixed(2),
                    date: creditRow.date || "",
                },

                credit: (
                    Number(creditRow.credit || 0) + addCredit
                ).toFixed(2),

                debit: "",
                date: "-",
            };
        }),

        // ✅ Entries tracking
        allEntries: {
            ...entry.allEntries,
            [periodKey]: [
                ...(entry.allEntries?.[periodKey] || []),
                JournalEntry,
            ],
        },
    };
};

// export const updatedCfsUniqueWPHandler = (modifiedData, periodDate) => {
//     let uniqeEntiesArray = [];
//     let customIndex = 0;

//     modifiedData.forEach((JournalEntry) => {
//         const { debitRows, creditRows } = JournalEntry;
//         const periodKey = JournalEntry.period === periodDate ? "periodOne" : "periodTwo";
//         const oppositePeriodKey = periodKey === "periodOne" ? "periodTwo" : "periodOne";

//         const debitRow = debitRows[0];
//         const creditRow = creditRows[0];
//         // ---------------- 1 debit - 1 credit ----------------
//         if (debitRows.length === 1 && creditRows.length === 1) {

//             const index =  uniqeEntiesArray.findIndex(
//                 (entry) =>
//                     normalize(entry?.debitRows[0]?.accountName) === normalize(debitRow.accountName) &&
//                     normalize(entry?.creditRows[0]?.accountName) === normalize(creditRow.accountName) &&
//                     entry?.debitRows?.length === 1 && entry?.creditRows?.length === 1
//             );

//             if (index !== -1) {
//                 uniqeEntiesArray[index] = mergeEntries({
//                 entry: uniqeEntiesArray[index],
//                 JournalEntry,
//                 debitRows,
//                 creditRows,
//                 periodKey
//             });
//             } else {
//                 customIndex += 1;
//                 uniqeEntiesArray.push({
//                     ...JournalEntry,
//                     index: customIndex,
//                     count: 1,
//                     debitRows: [
//                         {
//                             ...debitRow,
//                             [periodKey]: { debit: Number(debitRow.debit).toFixed(2), credit: "", date: debitRow.date || "" },
//                             [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                             debit: Number(debitRow.debit).toFixed(2),
//                             credit: "",
//                             cfsMethodInfo: {[periodKey]: (debitRow?.cfsMethodInfo || []), [oppositePeriodKey]: []}
//                         },
//                     ],
//                     creditRows: [
//                         {
//                             ...creditRow,
//                             [periodKey]: { debit: "", credit: Number(creditRow.credit).toFixed(2), date: creditRow.date || "" },
//                             [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                             credit: Number(creditRow.credit).toFixed(2),
//                             debit: ""
//                         },
//                     ],
//                     allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: "" },
//                 });
//             }
//         }

//         // ---------------- 1 debit - N credit ----------------
//         else if (debitRows.length === 1 && creditRows.length > 1) {

//             const index = uniqeEntiesArray.findIndex(
//                 (entry) =>
//                     entry.debitRows?.length === 1 &&
//                     entry.creditRows.length === creditRows.length &&
//                     normalize(entry.debitRows[0]?.accountName) === normalize(debitRow.accountName) &&
//                     isSameDebitStructure(entry.creditRows, creditRows)
//             );

//             if (index !== -1) {
//                 uniqeEntiesArray[index] = mergeEntries({
//                     entry: uniqeEntiesArray[index],
//                     JournalEntry,
//                     debitRows,
//                     creditRows,
//                     periodKey
//                 });
//             } else {
//                 customIndex += 1;
//                 uniqeEntiesArray.push({
//                     ...JournalEntry,
//                     index: customIndex,
//                     count: 1,
//                     debitRows: [
//                         {
//                             ...debitRow,
//                             [periodKey]: { debit: Number(debitRow.debit).toFixed(2), credit: "", date: debitRow.date || "" },
//                             [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                             debit: Number(debitRow.debit).toFixed(2),
//                             credit: "",
//                             cfsMethodInfo: {[periodKey]: (debitRow?.cfsMethodInfo || []), [oppositePeriodKey]: []}
//                         },
//                     ],
//                     creditRows: creditRows.map((row) => ({
//                         ...row,
//                         [periodKey]: { debit: "", credit: Number(row.credit).toFixed(2), date: row.date || "" },
//                         [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                         debit: "",
//                         credit: Number(row.credit).toFixed(2),
//                     })),
//                     allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: "" },
//                 });
//             }
//         }

//         // ---------------- N debit - 1 credit ----------------
//         else if (debitRows.length > 1 && creditRows.length === 1) {

//             const index = uniqeEntiesArray.findIndex((entry) =>
//                 entry?.creditRows?.length === 1 &&
//                 entry.debitRows.length === debitRows.length &&
//                 normalize(entry.debitRows[0]?.accountName) === normalize(debitRow.accountName) &&
//                 isSameDebitStructure(entry.debitRows, debitRows)
//             );

//             if (index !== -1) {
//                 uniqeEntiesArray[index] = mergeEntries({
//                     entry: uniqeEntiesArray[index],
//                     JournalEntry,
//                     debitRows,
//                     creditRows,
//                     periodKey
//                 });
//             } else {
//                 customIndex += 1;
//                 uniqeEntiesArray.push({
//                     ...JournalEntry,
//                     index: customIndex,
//                     count: 1,
//                     debitRows: debitRows.map((row) => ({
//                         ...row,
//                         [periodKey]: { debit: Number(row.debit).toFixed(2), credit: "", date: row.date || "" },
//                         [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                         debit: Number(row.debit).toFixed(2),
//                         credit: "",
//                         cfsMethodInfo: {
//                             [periodKey]: (row?.cfsMethodInfo || []),
//                             [oppositePeriodKey]: []
//                         }
//                     })),
//                     creditRows: [
//                         {
//                             ...creditRow,
//                             [periodKey]: { debit: "", credit: Number(creditRow.credit).toFixed(2), date: creditRow.date || "" },
//                             [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                             debit: "",
//                             credit: Number(creditRow.credit).toFixed(2),
//                         },
//                     ],
//                     allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: "" },
//                 });
//             }
//         }

//         // ---------------- N debit - N credit ----------------
//         else {

//             const index = uniqeEntiesArray.findIndex(
//                 (entry) =>
//                     entry.debitRows.length === debitRows.length &&
//                     entry.creditRows.length === creditRows.length &&
//                     isSameDebitStructure(entry.debitRows, debitRows) &&
//                     isSameDebitStructure(entry.creditRows, creditRows)
//             );

//             if (index !== -1) {
//                 uniqeEntiesArray[index] = mergeEntries({
//                     entry: uniqeEntiesArray[index],
//                     JournalEntry,
//                     debitRows,
//                     creditRows,
//                     periodKey
//                 });
//             } else {
//                 customIndex += 1;
//                 uniqeEntiesArray.push({
//                     ...JournalEntry,
//                     index: customIndex,
//                     count: 1,
//                     debitRows: debitRows.map((row) => ({
//                         ...row,
//                         [periodKey]: { debit: Number(row.debit).toFixed(2), credit: "", date: row.date || "" },
//                         [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                         debit: Number(row.debit).toFixed(2),
//                         credit: "",
//                         cfsMethodInfo: {
//                             [periodKey]: (row?.cfsMethodInfo || []),
//                             [oppositePeriodKey]: []
//                         }
//                     })),
//                     creditRows: creditRows.map((row) => ({
//                         ...row,
//                         [periodKey]: { debit: "", credit: Number(row.credit).toFixed(2), date: row.date || "" },
//                         [oppositePeriodKey]: { debit: "", credit: "", date: "" },
//                         debit: "",
//                         credit: Number(row.credit).toFixed(2),
//                     })),
//                     allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: "" },
//                 });
//             }
//         }
//     });
//     return uniqeEntiesArray;
// };

// Bug 1 fix: normalize account name keys so case-insensitive matching aligns with isSameDebitStructure
const buildIndexedMapFixed = (rows = []) => {
    const map = {};
    const counter = {};

    rows.forEach((row) => {
        const key = normalize(row.accountName);

        const index = counter[key] || 0;
        counter[key] = index + 1;

        if (!map[key]) map[key] = [];
        map[key][index] = row;
    });

    return map;
};

const mergeEntriesFixed = ({
    entry,
    JournalEntry,
    debitRows,
    creditRows,
    periodKey
}) => {
    const newDebitMap = buildIndexedMapFixed(debitRows);
    const newCreditMap = buildIndexedMapFixed(creditRows);

    const existingDebitMap = buildIndexedMapFixed(entry.debitRows);
    const existingCreditMap = buildIndexedMapFixed(entry.creditRows);

    return {
        ...entry,

        // Bug 2 fix: increment count for every merged entry regardless of which period it belongs to
        count: entry.count + 1,

        debitRows: entry.debitRows.map((debitRow) => {
            const key = normalize(debitRow.accountName);

            const occurrenceIndex = existingDebitMap[key]?.indexOf(debitRow);
            const matchedDebit = newDebitMap[key]?.[occurrenceIndex];
            const addDebit = Number(matchedDebit?.debit || 0);

            return {
                ...debitRow,

                [periodKey]: {
                    debit: (Number(debitRow[periodKey]?.debit || 0) + addDebit).toFixed(2),
                    credit: "",
                    date: debitRow.date || "",
                },

                debit: (Number(debitRow.debit || 0) + addDebit).toFixed(2),
                credit: "",
                date: "-",

                // Fix: use Math.max of both arrays so whichever period is non-empty drives
                // iteration — prevents periodTwo staying [] when first push only populated periodOne
                cfsMethodInfo: {
                    ...debitRow?.cfsMethodInfo,
                    [periodKey]: (() => {
                        const existing = debitRow?.cfsMethodInfo?.[periodKey] || [];
                        const incoming = matchedDebit?.cfsMethodInfo || [];
                        return Array.from(
                            { length: Math.max(existing.length, incoming.length) },
                            (_, idx) => ({
                                ...(existing[idx] || incoming[idx]),
                                amount: (
                                    Number(existing[idx]?.amount || 0) +
                                    Number(incoming[idx]?.amount || 0)
                                ).toFixed(2),
                            })
                        );
                    })(),
                },
            };
        }),

        creditRows: entry.creditRows.map((creditRow) => {
            const key = normalize(creditRow.accountName);

            const occurrenceIndex = existingCreditMap[key]?.indexOf(creditRow);
            const matchedCredit = newCreditMap[key]?.[occurrenceIndex];
            const addCredit = Number(matchedCredit?.credit || 0);

            return {
                ...creditRow,

                [periodKey]: {
                    debit: "",
                    credit: (Number(creditRow[periodKey]?.credit || 0) + addCredit).toFixed(2),
                    date: creditRow.date || "",
                },

                credit: (Number(creditRow.credit || 0) + addCredit).toFixed(2),
                debit: "",
                date: "-",
            };
        }),

        allEntries: {
            ...entry.allEntries,
            [periodKey]: [...(entry.allEntries?.[periodKey] || []), JournalEntry],
        },
    };
};

export const updatedCfsUniqueWPHandler = (modifiedData, periodDate) => {
    let uniqeEntiesArray = [];
    let customIndex = 0;

    modifiedData.forEach((JournalEntry) => {
        const { debitRows, creditRows } = JournalEntry;
        const periodKey = JournalEntry.period === periodDate ? "periodOne" : "periodTwo";
        const oppositePeriodKey = periodKey === "periodOne" ? "periodTwo" : "periodOne";

        const debitRow = debitRows[0];
        const creditRow = creditRows[0];

        // ---------------- 1 debit - 1 credit ----------------
        if (debitRows.length === 1 && creditRows.length === 1) {

            const index = uniqeEntiesArray.findIndex(
                (entry) =>
                    normalize(entry?.debitRows[0]?.accountName) === normalize(debitRow.accountName) &&
                    normalize(entry?.creditRows[0]?.accountName) === normalize(creditRow.accountName) &&
                    entry?.debitRows?.length === 1 && entry?.creditRows?.length === 1
            );

            if (index !== -1) {
                uniqeEntiesArray[index] = mergeEntriesFixed({
                    entry: uniqeEntiesArray[index],
                    JournalEntry,
                    debitRows,
                    creditRows,
                    periodKey
                });
            } else {
                customIndex += 1;
                uniqeEntiesArray.push({
                    ...JournalEntry,
                    index: customIndex,
                    count: 1,
                    debitRows: [
                        {
                            ...debitRow,
                            [periodKey]: { debit: Number(debitRow.debit).toFixed(2), credit: "", date: debitRow.date || "" },
                            [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                            debit: Number(debitRow.debit).toFixed(2),
                            credit: "",
                            cfsMethodInfo: { [periodKey]: (debitRow?.cfsMethodInfo || []), [oppositePeriodKey]: [] }
                        },
                    ],
                    creditRows: [
                        {
                            ...creditRow,
                            [periodKey]: { debit: "", credit: Number(creditRow.credit).toFixed(2), date: creditRow.date || "" },
                            [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                            credit: Number(creditRow.credit).toFixed(2),
                            debit: ""
                        },
                    ],
                    // Bug 3 fix: use [] instead of "" for the opposite period so consumers can safely call .length or spread
                    allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: [] },
                });
            }
        }

        // ---------------- 1 debit - N credit ----------------
        else if (debitRows.length === 1 && creditRows.length > 1) {

            const index = uniqeEntiesArray.findIndex(
                (entry) =>
                    entry.debitRows?.length === 1 &&
                    entry.creditRows.length === creditRows.length &&
                    normalize(entry.debitRows[0]?.accountName) === normalize(debitRow.accountName) &&
                    isSameDebitStructure(entry.creditRows, creditRows)
            );

            if (index !== -1) {
                uniqeEntiesArray[index] = mergeEntriesFixed({
                    entry: uniqeEntiesArray[index],
                    JournalEntry,
                    debitRows,
                    creditRows,
                    periodKey
                });
            } else {
                customIndex += 1;
                uniqeEntiesArray.push({
                    ...JournalEntry,
                    index: customIndex,
                    count: 1,
                    debitRows: [
                        {
                            ...debitRow,
                            [periodKey]: { debit: Number(debitRow.debit).toFixed(2), credit: "", date: debitRow.date || "" },
                            [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                            debit: Number(debitRow.debit).toFixed(2),
                            credit: "",
                            cfsMethodInfo: { [periodKey]: (debitRow?.cfsMethodInfo || []), [oppositePeriodKey]: [] }
                        },
                    ],
                    creditRows: creditRows.map((row) => ({
                        ...row,
                        [periodKey]: { debit: "", credit: Number(row.credit).toFixed(2), date: row.date || "" },
                        [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                        debit: "",
                        credit: Number(row.credit).toFixed(2),
                    })),
                    allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: [] },
                });
            }
        }

        // ---------------- N debit - 1 credit ----------------
        else if (debitRows.length > 1 && creditRows.length === 1) {

            const index = uniqeEntiesArray.findIndex((entry) =>
                entry?.creditRows?.length === 1 &&
                entry.debitRows.length === debitRows.length &&
                // Bug 4 fix: removed redundant entry.debitRows[0].accountName check — isSameDebitStructure covers all rows
                isSameDebitStructure(entry.debitRows, debitRows)
            );

            if (index !== -1) {
                uniqeEntiesArray[index] = mergeEntriesFixed({
                    entry: uniqeEntiesArray[index],
                    JournalEntry,
                    debitRows,
                    creditRows,
                    periodKey
                });
            } else {
                customIndex += 1;
                uniqeEntiesArray.push({
                    ...JournalEntry,
                    index: customIndex,
                    count: 1,
                    debitRows: debitRows.map((row) => ({
                        ...row,
                        [periodKey]: { debit: Number(row.debit).toFixed(2), credit: "", date: row.date || "" },
                        [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                        debit: Number(row.debit).toFixed(2),
                        credit: "",
                        cfsMethodInfo: {
                            [periodKey]: (row?.cfsMethodInfo || []),
                            [oppositePeriodKey]: []
                        }
                    })),
                    creditRows: [
                        {
                            ...creditRow,
                            [periodKey]: { debit: "", credit: Number(creditRow.credit).toFixed(2), date: creditRow.date || "" },
                            [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                            debit: "",
                            credit: Number(creditRow.credit).toFixed(2),
                        },
                    ],
                    allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: [] },
                });
            }
        }

        // ---------------- N debit - N credit ----------------
        else {

            const index = uniqeEntiesArray.findIndex(
                (entry) =>
                    entry.debitRows.length === debitRows.length &&
                    entry.creditRows.length === creditRows.length &&
                    isSameDebitStructure(entry.debitRows, debitRows) &&
                    isSameDebitStructure(entry.creditRows, creditRows)
            );

            if (index !== -1) {
                uniqeEntiesArray[index] = mergeEntriesFixed({
                    entry: uniqeEntiesArray[index],
                    JournalEntry,
                    debitRows,
                    creditRows,
                    periodKey
                });
            } else {
                customIndex += 1;
                uniqeEntiesArray.push({
                    ...JournalEntry,
                    index: customIndex,
                    count: 1,
                    debitRows: debitRows.map((row) => ({
                        ...row,
                        [periodKey]: { debit: Number(row.debit).toFixed(2), credit: "", date: row.date || "" },
                        [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                        debit: Number(row.debit).toFixed(2),
                        credit: "",
                        cfsMethodInfo: {
                            [periodKey]: (row?.cfsMethodInfo || []),
                            [oppositePeriodKey]: []
                        }
                    })),
                    creditRows: creditRows.map((row) => ({
                        ...row,
                        [periodKey]: { debit: "", credit: Number(row.credit).toFixed(2), date: row.date || "" },
                        [oppositePeriodKey]: { debit: "", credit: "", date: "" },
                        debit: "",
                        credit: Number(row.credit).toFixed(2),
                    })),
                    allEntries: { [periodKey]: [JournalEntry], [oppositePeriodKey]: [] },
                });
            }
        }
    });

    return uniqeEntiesArray;
};


export const sortFunction = (array, lowercasedInputP) => {
    // Convert selectedInput to lowercase for case-insensitive comparison
    const lowercasedInput = lowercasedInputP?.toLocaleLowerCase();

    // Flatten the debitRowArr array and filter based on the selectedInput
    const suggestions = array
        .flat(Infinity) // Flatten the array to handle nested structures
        .filter((item) => item?.toLowerCase()?.startsWith(lowercasedInput)) // Filter items containing the selected input
        .sort((a, b) => {
            const aLower = a?.toLowerCase();
            const bLower = b?.toLowerCase();

            // Check if 'a' starts with the search term and 'b' does not
            if (
                aLower?.startsWith(lowercasedInput) &&
                !bLower?.startsWith(lowercasedInput)
            ) {
                return -1; // 'a' should come before 'b'
            }

            // Check if 'b' starts with the search term and 'a' does not
            if (
                !aLower?.startsWith(lowercasedInput) &&
                bLower?.startsWith(lowercasedInput)
            ) {
                return 1; // 'b' should come before 'a'
            }

            // If both or neither start with the search term, sort alphabetically
            return aLower?.localeCompare(bLower);
        }); // Sort items alphabetically

    return suggestions;
};

// Utility function to format date
export const formatDate = (dateStr, fallback = "Invalid Date") => {
    const date = new Date(dateStr);
    if (isNaN(date) || !dateStr) return fallback;

    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
};

export const formatFullDate = (dateStr) => {
    return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat("MMMM d, yyyy");
};

export const generatePeriodHeadings = (data) => {
    let periodOne = { startDate: data?.periodOneFromDate, endDate: data?.periodOneToDate };
    let periodTwo = { startDate: data?.periodTwoFromDate, endDate: data?.periodTwoToDate };

    const isStartOfMonth = (dateStr) => {
        const date = DateTime.fromISO(dateStr, { zone: 'utc' });
        return date.day === 1;
    };

    const isEndOfMonth = (dateStr) => {
        const date = DateTime.fromISO(dateStr, { zone: 'utc' });
        return date.day === date.endOf('month').day;
    };

    // const periodOneStart = formatDate(periodOne.startDate);
    // const periodOneEnd = formatDate(periodOne.endDate);
    const periodOneEndFull = formatFullDate(periodOne.endDate);

    // const periodTwoStart = periodTwo?.startDate ? formatDate(periodTwo.startDate) : null;
    let periodTwoEnd = periodTwo?.endDate ? formatDate(periodTwo.endDate) : null;
    const periodTwoEndFull = periodTwo?.endDate ? formatFullDate(periodTwo.endDate) : null;

    let periodOneHeading = "";
    let periodTwoHeading = "";
    let mainHeading = "";

    const months = [
        "One", "Two", "Three", "Four", "Five", "Six",
        "Seven", "Eight", "Nine", "Ten", "Eleven"
    ];

    const calculateHeading = (startDate, endDate) => {
        const start = DateTime.fromISO(startDate, { zone: 'utc' });
        const end = DateTime.fromISO(endDate, { zone: 'utc' });

        if (!isStartOfMonth(startDate) || !isEndOfMonth(endDate)) {
            return "";
        }

        if (start.year === end.year) {
            const monthsDiff = end.month - start.month + 1;

            if (monthsDiff === 12) {
                return "Year Ended";
            } else if (monthsDiff === 1) {
                return "One Month Ended";
            } else {
                return `${months[monthsDiff - 1]} Months Ended`;
            }
        }

        return "";
    };

    if (periodOne.startDate && periodOne.endDate) {
        const start = DateTime.fromISO(periodOne.startDate, { zone: 'utc' });
        const end = DateTime.fromISO(periodOne.endDate, { zone: 'utc' });
        const diffInDays = end.diff(start, 'days').days;

        if (start.year !== end.year && diffInDays > 365) {
            periodOneHeading = "";
        } else {
            periodOneHeading = calculateHeading(periodOne.startDate, periodOne.endDate);
        }
    }

    if (periodTwo?.startDate && periodTwo?.endDate) {
        periodTwoHeading = calculateHeading(periodTwo.startDate, periodTwo.endDate);
        if (!periodTwoHeading) {
            periodTwoHeading = null;
            periodTwoEnd = `${formatDate(periodTwo.startDate)} To ${formatDate(periodTwo.endDate)}`;
        }
    } else {
        periodTwoHeading = null;
        periodTwoEnd = null;
    }

    if (periodOneHeading === periodTwoHeading && periodOneHeading !== "") {
        if (periodOneHeading === "Year Ended") {
            mainHeading = `${periodOneHeading.split(' ')[0]}s Ended`;
        } else {
            mainHeading = periodOneHeading;
        }
    }

    return {
        periodOneHeading,
        periodOneDate: periodOneHeading ? periodOneEndFull : `${formatDate(periodOne.startDate)} To ${formatDate(periodOne.endDate)}`,
        periodTwoHeading: periodTwoHeading || null,
        periodTwoDate: periodTwoHeading ? periodTwoEndFull : periodTwoEnd,
        mainHeading: mainHeading || null,
    };
};

export const getTotalBalances = (subEntries) => {
    const result = [];

    subEntries.forEach(entry => {
        entry.balance.forEach((value, index) => {
            result[index] = (result[index] || 0) + (+value || 0);
        });
    });
    return result;
};

export const getTotalBalancesFromEntries = (entries) => {
    const result = [];

    entries.forEach(entry => {
        entry.subEntries.forEach(sub => {
            sub.balance.forEach((value, index) => {
                result[index] = (result[index] || 0) + (+value || 0);
            });
        });
    });

    return result;
};


export const delay = (ms) => {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}
export function formatDateToPst(dateString, startingText) {
    const date = new Date(dateString);

    // Set options for the date formatter with time zone specified as PST
    const dateOptions = {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Los_Angeles' // PST time zone
    };
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);

    // Use Intl.DateTimeFormat to format time in PST
    const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'America/Los_Angeles'
    };
    const formattedTimeParts = new Intl.DateTimeFormat('en-US', timeOptions).formatToParts(date);

    // Extract the hour, minute, second, and AM/PM parts
    const hours = formattedTimeParts.find(part => part.type === 'hour')?.value;
    const minutes = formattedTimeParts.find(part => part.type === 'minute')?.value;
    const seconds = formattedTimeParts.find(part => part.type === 'second')?.value;
    const amPm = formattedTimeParts.find(part => part.type === 'dayPeriod')?.value;

    const formattedTime = `${hours}:${minutes}:${seconds} ${amPm}, PST`;
    return (
        <p>
            <span>{`${startingText || ""} ${formattedDate}`}, </span>{startingText && <br />}<span>{formattedTime}</span>
        </p>
    );
}

export const formatDateToEst = (dateString, startingText) => {
    // Parse input in UTC and convert to America/New_York timezone
    const estDate = DateTime.fromISO(dateString, { zone: "utc" }).setZone("America/New_York");

    // Format date: M/d/yyyy
    const formattedDate = estDate.toFormat("M/d/yyyy");

    // Format time: hh:mm:ss AM/PM
    const formattedTime = estDate.toFormat("HH:mm:ss");

    // Get the short timezone abbreviation manually using `.offsetNameShort`
    // This reliably returns 'EST' or 'EDT' depending on date
    // const timeZoneAbbr = estDate.offsetNameShort; // we will use this if we have to show EST/EDT dynamically

    return (
        <p>
            <span>{`${startingText || ""} ${formattedDate}`}, </span>
            {startingText && <br />}
            <span>{`${formattedTime}, ${"EST"}`}</span>
        </p>
    );
}

export const getAlphabetFromIndex = (index) => {
    let result = '';
    while (index >= 0) {
        result = String.fromCharCode((index % 26) + 97) + result;
        index = Math.floor(index / 26) - 1;
    }
    return result;
};

export const getPeriodFromAndToDate = (selectedPeriod, formDetail) => {  //This function is also used in TBAccountModal file but not imported.
    const period = (selectedPeriod === "periodOne" || selectedPeriod === 1)
        ? ["periodOneFromDate", "periodOneToDate"]
        : ["periodTwoFromDate", "periodTwoToDate"];

    const fromRaw = formDetail?.[period[0]];
    const toRaw = formDetail?.[period[1]];

    const fromDate = DateTime.fromISO(fromRaw, { zone: "utc" });
    const toDate = DateTime.fromISO(toRaw, { zone: "utc" });

    if (!fromDate.isValid || !toDate.isValid) {
        return "-";
    }

    return `${fromDate.toFormat("MM/dd/yyyy")} - ${toDate.toFormat("MM/dd/yyyy")}`;
};

// sortStrings.js ascending order a
export const sortStringsAscending = (arr = []) => {
    if (!Array.isArray(arr)) return [];

    return [...arr].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
};

// utils/sortObjectsByName.js
export const sortObjectsWithKey = (arr = [], key = "name") => {
    if (!Array.isArray(arr)) return [];

    return [...arr].sort((a, b) => {
        const nameA = a?.[key] || "";
        const nameB = b?.[key] || "";
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
};

export const getMonthDateRange = (monthShort, year = DateTime.now().year) => {
    // Map short month names to month numbers
    const monthMap = {
      Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
      Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
    };
  
    // Convert input like "jan" or "JAN" to "Jan" for consistent lookup
    const normalized = monthShort?.slice(0, 3)?.charAt(0).toUpperCase() + monthShort?.slice(1, 3)?.toLowerCase();
    const monthNum = monthMap[normalized];
  
    if (!monthNum) return "-"; // Invalid month
  
    // Create first and last day of the month in UTC
    const firstDay = DateTime.utc(year, monthNum, 1);
    const lastDay = firstDay.endOf("month");
  
    if (!firstDay.isValid || !lastDay.isValid) return "-";
  
    return `${firstDay.toFormat("MM/dd/yyyy")} - ${lastDay.toFormat("MM/dd/yyyy")}`;
};

export const isUploadedTypeIncluded = (type) => {
    return ["quickBook", "quickBook-excel"]?.includes(type);
};

//this is for filtering the schedule api data
export const hasBalance = (entry) => {
  const periods = entry?.balance ?? {};

  return Object.values(periods).some(period => {
    const debit = period?.debit ?? 0;
    const credit = period?.credit ?? 0;
    return debit !== 0 || credit !== 0;
  });
};

export const zeroOnlyEntries = (data) => {
    let updatedData = data?.filter(item =>
      item?.subHeader?.every(sub =>
        sub?.data?.every(value => value !== 0)
      )
    );
    return updatedData;
} 

export const addStaticEntriesHandler = (mainData, staticData) => {
    const filteredData = mainData?.body?.map(item => {
        const findStaticEntry = staticData?.find(stcEntries => stcEntries?.header === item?.header);
        if(findStaticEntry) {
            return {...item, subHeader: findStaticEntry?.subHeader, difference: findStaticEntry?.difference}
        } else {
            return item
        }
    });
    return filteredData;
}